import { ReactNode, useEffect, useRef, useState } from "react";
import { Button, Checkbox, Dropdown, Input, message, Modal } from "antd";
import { EditOutlined, MoreOutlined } from "@ant-design/icons";
import BrandTemplateApi, { BrandTemplateDto, TemplateVisibility } from "../../../../../api/BrandTemplateApi";
import DesignApi from "../../../../../api/DesignApi";
import { DesignDto } from "../../../../../generated/dashboard-client";
import { MenuMode } from "../../../../design/DesignCardMenu";
import useFocus from "../../../../../hooks/useFocus";
import BrandTemplateCardMenu from "./BrandTemplateCardMenu";
import { formatTemplateSize } from "../../../../../util/utils";
import TemplateStatusBadge from "../../../../common/TemplateStatusBadge";

interface Props {
  template: BrandTemplateDto;
  brandKitId: string;
  productLabel: string;
  thumb: ReactNode;
  onEdit: (template: BrandTemplateDto) => void;
  onRenamed: (id: string, name: string) => void;
  onDelete: (template: BrandTemplateDto) => void;
  onRemoved: (template: BrandTemplateDto) => void;
  onCopied: () => void;
  onCopyToBrandKit: (template: BrandTemplateDto) => void;
  onSetVisibility: (template: BrandTemplateDto, visibility: TemplateVisibility) => void;
  selectMode: boolean;
  selected: boolean;
  onSelect: (template: BrandTemplateDto, checked: boolean) => void;
  reorderMode?: boolean;
  orderNumber?: number;
  dragHandle?: ReactNode;
}

export default function SavedTemplateCard({
  template,
  brandKitId,
  productLabel,
  thumb,
  onEdit,
  onRenamed,
  onDelete,
  onRemoved,
  onCopied,
  onCopyToBrandKit,
  onSetVisibility,
  selectMode,
  selected,
  onSelect,
  reorderMode = false,
  orderNumber,
  dragHandle,
}: Props) {
  const dropdownButton = useRef<HTMLSpanElement>(null);
  const [menuVisible, setMenuVisible] = useState(false);
  const [mode, setMode] = useState(MenuMode.Root);
  const [copyLoading, setCopyLoading] = useState(false);
  const [downloadFetching, setDownloadFetching] = useState(false);
  const [design, setDesign] = useState<DesignDto | null>(null);

  const [isRenaming, setRenaming] = useState(false);
  const [name, setName] = useState(template.name);
  const [renameInputRef, setRenameInputFocus] = useFocus();

  useEffect(() => {
    setName(template.name);
  }, [template]);

  const sizeText = formatTemplateSize(template.productType, template.width, template.height, template.sizeUnit);

  const closeMenu = () => setMenuVisible(false);
  const hideDropdown = () => dropdownButton.current?.click();

  function toggleMenu() {
    if (!menuVisible) setMode(MenuMode.Root);
    setMenuVisible(!menuVisible);
  }

  function onVisibleChange(visible: boolean) {
    if (visible) setMode(MenuMode.Root);
  }

  function handleEdit() {
    closeMenu();
    onEdit(template);
  }

  function startRenaming() {
    if (reorderMode) return;
    setRenaming(true);
    (setRenameInputFocus as () => void)();
  }

  function rename() {
    setRenaming(false);
    const trimmed = name.trim();
    if (!trimmed || trimmed === template.name) {
      setName(template.name);
      return;
    }
    if (!template.id) return;
    BrandTemplateApi.rename(template.id, trimmed)
      .then((resp) => {
        if (!resp.ok) {
          setName(template.name);
          message.error("Failed to rename template");
          return;
        }
        onRenamed(template.id!, trimmed);
      })
      .catch(() => {
        setName(template.name);
        message.error("Failed to rename template");
      });
  }

  function handleDelete() {
    closeMenu();
    onDelete(template);
  }

  function handleCopyToBrandKit() {
    closeMenu();
    onCopyToBrandKit(template);
  }

  function handleMakeLive() {
    closeMenu();
    onSetVisibility(template, "live");
  }

  function handleRevertToDraft() {
    closeMenu();
    onSetVisibility(template, "draft");
  }

  function onThumbClick() {
    if (reorderMode) return;
    if (selectMode) {
      onSelect(template, !selected);
      return;
    }
    onEdit(template);
  }

  function handleRemoveFromBrandTemplates() {
    if (!template.id) return;
    closeMenu();
    Modal.confirm({
      title: "Remove from brand kit?",
      content:
        "This design will move back to Saved Designs. It won't be available as a template anymore, but nothing else will change.",
      okText: "Remove from Brand Templates",
      onOk: async () => {
        try {
          await BrandTemplateApi.removeFromBrandTemplates(template.id!);
        } catch {
          message.error("Failed to remove from brand templates");
          return Promise.reject();
        }
        message.success("Removed from brand templates");
        onRemoved(template);
      },
    });
  }

  function handleCopy() {
    if (!template.id || !brandKitId || copyLoading) return;
    setCopyLoading(true);
    BrandTemplateApi.copy(brandKitId, template.id, `Copy of ${template.name}`)
      .then((resp) => {
        if (!resp.ok) throw new Error();
        message.success("Template copied");
        setMenuVisible(false);
        onCopied();
      })
      .catch(() => message.error("Failed to copy template"))
      .finally(() => setCopyLoading(false));
  }

  async function handleDownloadClick() {
    if (!template.id) return;
    if (design) {
      setMode(MenuMode.Download);
      return;
    }
    setDownloadFetching(true);
    try {
      const resp = await DesignApi.getLightDesign(template.id);
      setDesign(resp.data);
      setMode(MenuMode.Download);
    } catch {
      message.error("Failed to open download options");
    } finally {
      setDownloadFetching(false);
    }
  }

  const menu = (
    <BrandTemplateCardMenu
      template={template}
      productLabel={productLabel}
      design={design}
      mode={mode}
      setMode={setMode}
      isCopyLoading={copyLoading}
      isDownloadFetching={downloadFetching}
      onEdit={handleEdit}
      onCopy={handleCopy}
      onCopyToBrandKit={handleCopyToBrandKit}
      onDownloadClick={handleDownloadClick}
      onRemoveFromBrandTemplates={handleRemoveFromBrandTemplates}
      onMakeLive={handleMakeLive}
      onRevertToDraft={handleRevertToDraft}
      onDelete={handleDelete}
      closeMenu={closeMenu}
      hideDropdown={hideDropdown}
    />
  );

  return (
    <div
      className={`template-saved-card${selected ? " selected" : ""}${selectMode ? " select-mode" : ""}${
        reorderMode ? " reorder-mode" : ""
      }`}
    >
      {reorderMode ? (
        <>
          <div className="template-saved-card-drag">{dragHandle}</div>
          {orderNumber != null && <div className="template-saved-card-order">{orderNumber}</div>}
        </>
      ) : (
        <>
          <div className="template-saved-card-select" onClick={(ev) => ev.stopPropagation()}>
            <Checkbox
              checked={selected}
              onChange={(ev) => onSelect(template, ev.target.checked)}
            />
          </div>
          <Dropdown
            className="desktop-design-menu more-btn-ops"
            visible={menuVisible}
            overlay={menu}
            overlayClassName="template-saved-card-menu-overlay"
            onVisibleChange={onVisibleChange}
          >
            <MoreOutlined ref={dropdownButton} onClick={toggleMenu} />
          </Dropdown>
        </>
      )}
      <div
        className="template-saved-card-thumb"
        role="button"
        tabIndex={0}
        onClick={onThumbClick}
      >
        {thumb}
      </div>
      <div className="template-saved-card-footer">
        <div className="template-saved-card-text">
          {!isRenaming && (
            <div className="template-saved-card-name-row" onClick={startRenaming}>
              <h6 className="template-saved-card-name" title={name}>
                {name}
              </h6>
              <Button type="link" className="rename-trigger">
                <EditOutlined />
              </Button>
            </div>
          )}
          <div className={`template-saved-card-name-edit ${isRenaming ? "show" : ""}`}>
            <Input
              ref={renameInputRef}
              size="small"
              value={name}
              onChange={(ev) => setName(ev.target.value)}
              onBlur={rename}
              onPressEnter={(ev) => (ev.target as HTMLInputElement).blur()}
            />
          </div>
          <div className="template-saved-card-type-row">
            <TemplateStatusBadge visibility={template.visibility} className="template-saved-card-badge" />
            <span className="template-saved-card-type">
              <span className="template-saved-card-type-label">{productLabel}</span>
              {sizeText && ` • ${sizeText}`}
            </span>
          </div>
          {template.lastEditorName && (
            <div className="template-saved-card-editor" title={`Last edited by ${template.lastEditorName}`}>
              Edited by {template.lastEditorName}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
